import type { ApiResult } from '../../../types';
import type { RightToInformationWizardDraft } from '../../../types/rightToInformationCaseRequest';
import type { RightToInformationCoverNoteResponse } from '../../../types/rightToInformationCoverNoteApi';
import { caseApiPdfRequest, caseApiRequest } from '../../../services/apiClient';
import { FILE_CATEGORY_RIGHT_TO_INFORMATION } from './rightToInformationConstants';

function strField(raw: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const v = raw[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function nullableStrField(raw: Record<string, unknown>, ...keys: string[]): string | null {
  for (const key of keys) {
    const v = raw[key];
    if (typeof v === 'string') {
      const t = v.trim();
      return t || null;
    }
    if (v === null) return null;
  }
  return null;
}

function documentNamesFromApi(docs: unknown): string[] {
  if (!Array.isArray(docs)) return [];
  return docs
    .map((entry) => {
      if (typeof entry === 'string') return entry.trim();
      if (!entry || typeof entry !== 'object') return '';
      const row = entry as Record<string, unknown>;
      for (const key of [
        'document_type_name',
        'documentTypeName',
        'document_name',
        'documentName',
        'name',
        'label',
        'title',
        'original_file_name',
        'originalFileName',
        'file_name',
        'fileName',
      ]) {
        const value = row[key];
        if (typeof value === 'string' && value.trim()) return value.trim();
      }
      return '';
    })
    .filter(Boolean);
}

function documentsFromCoverNoteApi(raw: Record<string, unknown>): string[] {
  for (const key of ['documents', 'documents_declared', 'documentsDeclared', 'declared_documents']) {
    const value = raw[key];
    if (value == null) continue;
    const parsed = documentNamesFromApi(value);
    if (parsed.length) return parsed;
    if (Array.isArray(value) && value.length === 0) return [];
  }
  return [];
}

export function normalizeRightToInformationCoverNoteResponse(
  raw: unknown,
): RightToInformationCoverNoteResponse | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;

  return {
    fileReferenceNumber: strField(r, 'fileReferenceNumber', 'file_reference_number'),
    requestSubmittedAt: strField(r, 'requestSubmittedAt', 'request_submitted_at'),
    submittedDivisionName: nullableStrField(
      r,
      'submittedDivisionName',
      'submitted_division_name',
      'division_name',
    ),
    submittedAgency: nullableStrField(r, 'submittedAgency', 'submitted_agency'),
    fileCategory: strField(r, 'fileCategory', 'file_category') || FILE_CATEGORY_RIGHT_TO_INFORMATION,
    requestorName: nullableStrField(r, 'requestorName', 'requestor_name'),
    specificPublicAuthorityName: nullableStrField(
      r,
      'specificPublicAuthorityName',
      'specific_public_authority_name',
    ),
    urgencyLevel: nullableStrField(r, 'urgencyLevel', 'urgency_level'),
    deadline: nullableStrField(r, 'deadline'),
    urgencyJustification: nullableStrField(r, 'urgencyJustification', 'urgency_justification'),
    documents: documentsFromCoverNoteApi(r),
    qr: strField(r, 'qr'),
  };
}

function pickString(primary: string | null | undefined, alt: string | null | undefined): string | null {
  const p = primary?.trim();
  if (p) return p;
  const a = alt?.trim();
  return a || null;
}

function pickDocuments(primary: string[], alt?: string[]): string[] {
  if (primary.length) return primary;
  if (alt?.length) return alt;
  return [];
}

export function mergeRightToInformationCoverNoteResponse(
  api: RightToInformationCoverNoteResponse,
  fallback?: Partial<RightToInformationCoverNoteResponse>,
): RightToInformationCoverNoteResponse {
  if (!fallback) return api;

  return {
    fileReferenceNumber: api.fileReferenceNumber.trim() || fallback.fileReferenceNumber?.trim() || '',
    requestSubmittedAt: api.requestSubmittedAt.trim() || fallback.requestSubmittedAt?.trim() || '',
    submittedDivisionName: pickString(api.submittedDivisionName, fallback.submittedDivisionName ?? null),
    submittedAgency: pickString(api.submittedAgency, fallback.submittedAgency ?? null),
    fileCategory: api.fileCategory.trim() || fallback.fileCategory?.trim() || FILE_CATEGORY_RIGHT_TO_INFORMATION,
    requestorName: pickString(api.requestorName, fallback.requestorName ?? null),
    specificPublicAuthorityName: pickString(
      api.specificPublicAuthorityName,
      fallback.specificPublicAuthorityName ?? null,
    ),
    urgencyLevel: pickString(api.urgencyLevel, fallback.urgencyLevel ?? null),
    deadline: pickString(api.deadline, fallback.deadline ?? null),
    urgencyJustification: pickString(api.urgencyJustification, fallback.urgencyJustification ?? null),
    documents: pickDocuments(api.documents, fallback.documents),
    qr: api.qr.trim() || fallback.qr?.trim() || '',
  };
}

export function rightToInformationDraftToCoverNoteFallback(
  draft: RightToInformationWizardDraft,
  options?: {
    fileRef?: string;
    submittedDivisionName?: string | null;
    submittedAgency?: string | null;
    submittedAt?: string;
  },
): Partial<RightToInformationCoverNoteResponse> {
  const documents = draft.documents
    .map((d) => d.documentName.trim() || d.fileName.trim() || d.originalFileName.trim())
    .filter(Boolean);

  return {
    fileReferenceNumber: options?.fileRef?.trim() || draft.referenceNo.trim() || undefined,
    requestSubmittedAt: options?.submittedAt?.trim() || undefined,
    submittedDivisionName:
      options?.submittedDivisionName?.trim() || draft.submittedDivisionName.trim() || null,
    submittedAgency: options?.submittedAgency?.trim() || draft.submittedAgencyName.trim() || null,
    fileCategory: draft.fileCategory.trim() || FILE_CATEGORY_RIGHT_TO_INFORMATION,
    requestorName: draft.requestorName.trim() || null,
    specificPublicAuthorityName: draft.specificPublicAuthorityName.trim() || null,
    urgencyLevel: draft.urgencyLevelName.trim() || null,
    deadline: draft.deadline.trim() || null,
    urgencyJustification: draft.urgencyJustification.trim() || null,
    documents,
  };
}

/** CMS-CRC-30 — GET /v1/case-requests/cover-note/{caseRequestId} */
export async function getRightToInformationCoverNote(
  caseRequestId: string,
): Promise<ApiResult<RightToInformationCoverNoteResponse>> {
  const id = caseRequestId.trim();
  if (!id) return { success: false, message: 'Case request id is required.' };

  const res = await caseApiRequest<unknown>(`/cover-note/${encodeURIComponent(id)}`);
  if (!res.ok) return { success: false, message: res.message };

  const data = normalizeRightToInformationCoverNoteResponse(res.data);
  if (!data) return { success: false, message: 'Cover note response is invalid.' };
  return { success: true, data };
}

/** CMS-CRC-31 — GET /v1/case-requests/cover-note/pdf/{caseRequestId} */
export async function downloadRightToInformationCoverNotePdf(
  caseRequestId: string,
): Promise<ApiResult<Blob>> {
  const id = caseRequestId.trim();
  if (!id) return { success: false, message: 'Case request id is required.' };

  const res = await caseApiPdfRequest(`/cover-note/pdf/${encodeURIComponent(id)}`);
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: res.data };
}
